import React from 'react';
import { useTheme } from '@mui/material/styles';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import ListItem from '@mui/material/ListItem';
import Button from '@mui/material/Button';

import { NavLink as RouterLink } from 'react-router';
import Link from '@mui/material/Link';
import { getPillColor, knownOfflineTag } from './helpers';
import { themeProps } from '../../styles/theme';
import { useAppSelector } from '../../state/store';
import { getLocationsById, getSensorsById } from '../../state/selectors';
import { ListOptions } from '../../Views/OfflineView';
import { DataTreeItem } from '../../services/api';

interface OfflineLocSummaryProps {
  locId: string;
  listType: ListOptions;
  locSensors: DataTreeItem[];
  offlineSensors: DataTreeItem[];
  handleTagging: (sensors: DataTreeItem[], isTagged: boolean) => void;
  children?: React.ReactChild;
}

function OfflineLocSummary({
  locId,
  listType,
  locSensors,
  offlineSensors,
  handleTagging,
  children,
}: OfflineLocSummaryProps): JSX.Element {
  const theme = useTheme();
  const locationsById = useAppSelector(getLocationsById);
  const sensorsById = useAppSelector(getSensorsById);
  const parentLoc = locId.substring(0, locId.lastIndexOf('#'));
  const parentLocName = parentLoc === '#' ? '' : locationsById.get(parentLoc)?.name ?? '';
  const isLocOffline = locSensors.length === offlineSensors.length;

  const isTagged =
    locSensors.filter((sensor) => sensorsById.get(sensor.id)?.tags?.includes(knownOfflineTag))
      .length === offlineSensors.length;

  return (
    <ListItem
      sx={{
        background: theme.palette.primary.main,
        borderRadius: '8px',
        margin: '8px 5px',
        padding: '16px',
        display: `${isTagged && listType === ListOptions.untagged ? 'none' : 'block'}`,
      }}
    >
      <Grid container spacing={2} sx={{ width: '100%', display: 'flex', alignItems: 'center' }}>
        <Grid size={{ sm: 4 }}>
          <Link component={RouterLink} to={`/location/${encodeURIComponent(locId)}`}>
            <Typography variant="h6">
              {parentLocName ? `${parentLocName} - ` : ''}
              {locationsById.get(locId)?.name ?? ''}
            </Typography>{' '}
          </Link>
        </Grid>
        <Grid size={{ sm: 2 }}>
          {/* bgcolor can be dynamic, like based on pct, red for most offline */}
          <span
            style={{
              background: getPillColor(offlineSensors.length, locSensors.length),
              padding: '5px 10px',
              borderRadius: '20px',
              color: themeProps.colors.white,
            }}
          >
            <b>{offlineSensors.length}</b> / <b>{locSensors.length} </b>{' '}
          </span>
          <span style={{ marginLeft: '10px' }}>{isLocOffline ? 'All Offline' : 'Offline'}</span>
        </Grid>
        <Grid size={{ sm: 2 }} sx={{ textAlign: 'center' }}>
          <b>Last Battery Pct</b>
        </Grid>
        <Grid size={{ sm: 2 }} sx={{ textAlign: 'center' }}>
          <b>Last Seen</b>
        </Grid>
        {isLocOffline && (
          <Grid size={{ sm: 2 }} sx={{ textAlign: 'end' }}>
            <Button
              onClick={() => handleTagging(locSensors ?? [], isTagged)}
              variant={themeProps.btnVariant.default}
            >
              {isTagged ? 'isTagged' : 'Click to Tag'}
            </Button>
          </Grid>
        )}
      </Grid>

      {children}
    </ListItem>
  );
}

OfflineLocSummary.defaultProps = {
  children: undefined,
};

export default OfflineLocSummary;
